import { useId, useMemo, useState } from 'react';
import LegOrderHeader from '@components/Modals/Headers/LegOrderHeader';
import { DialogContent } from '@mui/material';
import { useAppSelector } from '../Infrastructure/store';
import { useFetchLegQuery } from '../Api/Trinium/Endpoints/Leg';
import { usePutLegsMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { PutLegsResponse } from '@api/Trinium/Endpoints/Leg/types';
import { BulkMakeContainerAvailableProps, MakeContainerAvailableFormik, MakeContainerAvailableProps } from './types';
import { Formik, FormikHelpers } from 'formik';
import FormSkeleton from './FormSkeleton';
import { useModalProvider } from '@components/Modals/ModalContext';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import FormikModal from '@components/Modals/FormikModal';
import FormikForm from './FormikForm';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import { containerApiFieldMapping, emptyContainerForm, mapContainerToMutableLeg, mapLegToContainerForm } from '@forms/Container/types';
import { setFormFieldError } from '../Forms/Components/types';
import { SendLegMutationErrorResponse, sendLegMutations } from '@api/Trinium/Endpoints/Leg/actions';
import FormikModalHeaderSkeleton from '@components/Modals/FormikModalHeaderSkeleton';
import LegOrderHeaderSkeleton from '@components/Modals/Headers/LegOrderHeaderSkeleton';
import { getApiErrorMessage } from '@api/getApiError';
import { apiIsoStringFormat, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { Dayjs } from 'dayjs';

import styles from './MakeContainerAvailable.module.css';

type Props = MakeContainerAvailableProps | BulkMakeContainerAvailableProps;

function isBulkProps(props: Props): props is BulkMakeContainerAvailableProps {
	return 'legs' in props;
}

/** The common value across every leg for a date field, or null when they differ or none is set - mirrors how MultiContainerUpdate seeds its shared field */
function commonAvailableDate(legs: DispatchLeg[], field: 'fullAvailableDate' | 'emptyAvailableDate' | 'chassisAvailableDate') {
	const [first, ...rest] = legs.map((leg) => leg[field]?.unix);
	return rest.every((unix) => unix === first) ? parseDayJs(first) : null;
}

/** Whether the user actually edited a bulk date field, so an untouched pre-filled value never gets resent to the API */
function dateFieldChanged(current: Dayjs | null, initial: Dayjs | null): boolean {
	if (!current && !initial) return false;
	if (!current || !initial) return true;
	return !current.isSame(initial);
}

/** Order number + leg identity (e.g. "4782299/001 (2-ECL)"), so a bulk save error can say which leg it came from */
function describeLeg(leg: DispatchLeg): string {
	return `${String(leg.jobId)}/${leg.csnNo.toString().padStart(3, '0')} (${String(leg.legNo)}-${leg.legType ?? '?'})`;
}

export default function MakeContainerAvailable(props: Props) {
	const formId = useId();
	const [isOpen, setIsOpen] = useState(true);
	const terminalNumber = useAppSelector((state) => String(state.applicationState.selectedTerminal?.number));
	const modalContext = useModalProvider();
	const { queueModalAnnouncement } = modalContext;
	const [putLegs] = usePutLegsMutation();

	const { data: leg, isFetching } = useFetchLegQuery(
		{
			terminalNumber: terminalNumber,
			id: isBulkProps(props) ? 0 : props.legId
		},
		{
			refetchOnMountOrArgChange: true,
			skip: isBulkProps(props)
		}
	);

	const bulkLegs = isBulkProps(props) ? props.legs : undefined;

	const bulkLegCapability = useMemo(() => {
		if (!bulkLegs) return undefined;

		const fclLegIds = bulkLegs.filter((selectedLeg) => selectedLeg.legType === 'FCL').map((selectedLeg) => selectedLeg.legId);
		const eclLegIds = bulkLegs.filter((selectedLeg) => selectedLeg.legType === 'ECL').map((selectedLeg) => selectedLeg.legId);

		return {
			fclLegIds,
			eclLegIds,
			// Chassis Available only succeeds on FCL/ECL legs too - UpdateAvailability rejects any other leg type outright.
			// A field is only enabled when every selected leg matches - a mixed selection can't be partially bulk-updated.
			allFcl: fclLegIds.length === bulkLegs.length,
			allEcl: eclLegIds.length === bulkLegs.length,
			allChassisCapable: fclLegIds.length + eclLegIds.length === bulkLegs.length
		};
	}, [bulkLegs]);

	const initialValues: MakeContainerAvailableFormik | undefined = useMemo(() => {
		if (bulkLegs) {
			return {
				containerForm: {
					...emptyContainerForm,
					fullAvailableDate: commonAvailableDate(bulkLegs, 'fullAvailableDate'),
					emptyAvailableDate: commonAvailableDate(bulkLegs, 'emptyAvailableDate'),
					chassisAvailableDate: commonAvailableDate(bulkLegs, 'chassisAvailableDate')
				}
			};
		}

		if (!leg || isFetching) return undefined;

		return {
			containerForm: mapLegToContainerForm(leg)
		};
	}, [bulkLegs, leg, isFetching]);

	const onFormSubmit = async (values: MakeContainerAvailableFormik, formikHelpers: FormikHelpers<MakeContainerAvailableFormik>) => {
		const validationErrors = await formikHelpers.validateForm(values);
		if (!isFormikErrorsEmpty(validationErrors) || !initialValues) return;

		if (isBulkProps(props) && bulkLegCapability) {
			const { fullAvailableDate, emptyAvailableDate, chassisAvailableDate } = values.containerForm;
			const initialContainerForm = initialValues.containerForm;
			const fullAvailableChanged = dateFieldChanged(fullAvailableDate, initialContainerForm.fullAvailableDate);
			const emptyAvailableChanged = dateFieldChanged(emptyAvailableDate, initialContainerForm.emptyAvailableDate);
			const chassisAvailableChanged = dateFieldChanged(chassisAvailableDate, initialContainerForm.chassisAvailableDate);

			if (!fullAvailableChanged && !emptyAvailableChanged && !chassisAvailableChanged) {
				queueModalAnnouncement({
					severity: 'error',
					message: 'You must change at least one date value.'
				});
				return;
			}

			const { fclLegIds, eclLegIds } = bulkLegCapability;
			const legById = new Map(props.legs.map((selectedLeg) => [selectedLeg.legId, selectedLeg]));

			// Tracks which leg ids each request covers, so a failure can still name the leg it came from.
			const requests: { promise: Promise<PutLegsResponse>; legIds: number[] }[] = [];

			if (chassisAvailableChanged && chassisAvailableDate) {
				props.legs.forEach((selectedLeg) => {
					const isFcl = selectedLeg.legType === 'FCL';
					const isEcl = selectedLeg.legType === 'ECL';
					if (!isFcl && !isEcl) return; // Not chassis-capable - already reflected in allChassisCapable/disabled state.

					const preservedAvailDate = isFcl
						? fullAvailableChanged
							? fullAvailableDate
							: parseDayJs(selectedLeg.fullAvailableDate?.unix)
						: emptyAvailableChanged
							? emptyAvailableDate
							: parseDayJs(selectedLeg.emptyAvailableDate?.unix);

					requests.push({
						legIds: [selectedLeg.legId],
						promise: putLegs({
							terminalNumber,
							actionType: {
								action: 'setAvailability',
								data: {
									legIds: [selectedLeg.legId],
									...(preservedAvailDate && { availDate: preservedAvailDate.format(apiIsoStringFormat) }),
									chassisAvailDate: chassisAvailableDate.format(apiIsoStringFormat)
								}
							}
						}).unwrap()
					});
				});
			} else {
				// Full/Empty Available only ever apply to a leg of the matching type
				if (fullAvailableDate && fullAvailableChanged && fclLegIds.length > 0) {
					requests.push({
						legIds: fclLegIds,
						promise: putLegs({
							terminalNumber,
							actionType: {
								action: 'setAvailability',
								data: { legIds: fclLegIds, availDate: fullAvailableDate.format(apiIsoStringFormat) }
							}
						}).unwrap()
					});
				}

				if (emptyAvailableDate && emptyAvailableChanged && eclLegIds.length > 0) {
					requests.push({
						legIds: eclLegIds,
						promise: putLegs({
							terminalNumber,
							actionType: {
								action: 'setAvailability',
								data: { legIds: eclLegIds, availDate: emptyAvailableDate.format(apiIsoStringFormat) }
							}
						}).unwrap()
					});
				}
			}

			const results = await Promise.allSettled(requests.map((request) => request.promise));

			/** Leg identity ("<order#> (<leg#>-<type>)") for a legId, falling back to the raw id if it's somehow not one of the selected legs */
			const legLabel = (legId: number) => {
				const matchingLeg = legById.get(legId);
				return matchingLeg ? describeLeg(matchingLeg) : `Leg ${String(legId)}`;
			};

			// Every leg's result is reported individually, prefixed with which leg it's for - one order failing never blocks the rest.
			const errorMessages = results.flatMap((result, index) => {
				if (result.status === 'fulfilled') {
					return result.value.errors.map((error) => `${legLabel(error.legId)}: ${error.errorMessage}`);
				}

				const failedLegLabels = requests[index].legIds.map(legLabel).join(', ');
				return [`${failedLegLabels}: ${getApiErrorMessage(result.reason)}`];
			});
			errorMessages.forEach((message) => {
				queueModalAnnouncement({ severity: 'error', message });
			});

			// Keep the modal open when something failed, matching the single-order path, so the error is
			// actually seen rather than being discarded the instant the modal (and its announcement center) closes.
			if (errorMessages.length === 0) {
				setIsOpen(false);
			}
			return;
		}

		if (leg) {
			const asMutableLeg = {
				id: leg.id,
				...mapContainerToMutableLeg(values.containerForm, initialValues.containerForm)
			};

			await sendLegMutations(asMutableLeg, containerApiFieldMapping)
				.then(() => {
					setIsOpen(false);
				})
				.catch(
					// eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
					({ errors }: SendLegMutationErrorResponse) => {
						errors.forEach((error) => {
							if (error.fieldName) {
								setFormFieldError(error.fieldName, error.errorMessage, formikHelpers.setFieldError);
							} else {
								queueModalAnnouncement({
									message: error.errorMessage,
									severity: 'error'
								});
							}
						});
					}
				);
		}
	};

	// For some unknown reason we didn't find the leg
	if (!isBulkProps(props) && !isFetching && !leg) {
		modalContext.queueModalAnnouncement({
			severity: 'error',
			message: `Could not find leg identifier ${String(props.legId)}. Please refresh and try again.`
		});
		return <></>;
	}

	const title = isBulkProps(props) ? `Manage Container Availability (${String(props.legs.length)} Legs Selected)` : 'Manage Container Availability';

	return (
		<FormikModal
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
			}}
		>
			{initialValues ? (
				<Formik
					initialValues={initialValues}
					onSubmit={onFormSubmit}
				>
					<>
						<FormikModalHeader
							formName={formId}
							onClose={() => {
								setIsOpen(false);
							}}
							title={title}
						/>

						{!isBulkProps(props) && (
							<LegOrderHeader
								terminalNumber={terminalNumber}
								legId={props.legId}
							/>
						)}

						<DialogContent>
							<FormikForm
								id={formId}
								leg={leg}
								bulk={isBulkProps(props)}
								bulkFullAvailableDisabled={bulkLegCapability && !bulkLegCapability.allFcl}
								bulkEmptyAvailableDisabled={bulkLegCapability && !bulkLegCapability.allEcl}
								bulkChassisAvailableDisabled={bulkLegCapability && !bulkLegCapability.allChassisCapable}
							/>
						</DialogContent>
					</>
				</Formik>
			) : (
				<>
					<FormikModalHeaderSkeleton
						title={title}
						onClose={() => {
							setIsOpen(false);
						}}
					/>
					<LegOrderHeaderSkeleton />
					<DialogContent>
						<div className={styles.form}>
							<FormSkeleton />
						</div>
					</DialogContent>
				</>
			)}
		</FormikModal>
	);
}
